
/**
 * EmbeddingsGroup.jsx
 *
 * Handles all 3D objects (points, lines, curve, labels) for the embedding visualization.
 * Adds and updates Three.js objects in the scene based on embedding data and display parameters.
 *
 * Author: [Your Name]
 * Date: [Update as needed]
 */
// src/components/EmbeddingsGroup.jsx

import React, { useEffect, useRef } from 'react';
import { DisplayParamTokenColorMode, DisplayParamFogColorMode, DisplayParamEnvironmentColorMode, DisplayParamLabelColorMode, DisplayParamColors } from '../constants';
import * as THREE from 'three';
import CustomPointHelper from '../objects/CustomPointHelper';
import { Text } from 'troika-three-text';

// Example using Google Fonts CDN (Roboto Regular)
const robotoFontUrl = 'https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxP.ttf';

/**
 * EmbeddingsGroup
 *
 * Imperative Three.js component for rendering all 3D objects (points, lines, curve, labels) for the embedding visualization.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.sceneRef - Ref to the Three.js scene
 * @param {Array} props.enrichedChunks - Array of enriched chunk data
 * @param {number} [props.labelOffset] - Vertical offset for labels
 * @param {function} [props.getTokenColor] - Function to get color for a token id
 * @param {function} [props.getSequenceColor] - Function to get color for a token index in sequence
 * @param {string} [props.displayParam_tokenColorMode] - Coloring mode for display parameters ('sequence', 'id', etc.)
 * @param {boolean} [props.showLabels] - Whether to show first-word labels
 * @param {number} [props.textSize] - Size of text labels
 * @param {number} [props.dotSize] - Size of points
 * @param {string} [props.pointStyle] - Point style ('circle', 'ring', 'cloud')
 * @param {string} [props.showPathMode] - Path display mode ('curve', 'lines', 'none')
 * @param {string} [props.displayParam_fogColorMode] - Fog color mode (enum value)
 * @returns {null}
 */
function EmbeddingsGroup({
  sceneRef,
  threeSceneManagerRef, // <-- Add ref to ThreeSceneManager if available
  enrichedChunks = [],
  displayParam_tokenColorMode = DisplayParamTokenColorMode.SEQUENCE,
  displayParam_fogColorMode = DisplayParamFogColorMode.NONE,
  displayParam_environmentColorMode = DisplayParamEnvironmentColorMode.BLACK,
  displayParam_labelColorMode = DisplayParamLabelColorMode.SEQUENCE,
  showLabels = true,
  textSize = 2,
  dotSize = 1,
  labelOffset = 0.0,
  showPathMode = 'curve',
  getTokenColor,
  getSequenceColor,
  pointStyle = 'circle',
}) {
  // Debug: log all props on every render
  console.debug('[EmbeddingsGroup][render] props:', {
    enrichedChunksLen: enrichedChunks.length,
    displayParam_tokenColorMode,
    displayParam_labelColorMode,
    displayParam_fogColorMode,
    displayParam_environmentColorMode,
    showLabels,
    textSize,
    dotSize,
    labelOffset,
    showPathMode,
    pointStyle
  });
  const groupRef = useRef();
  const labelMeshesRef = useRef([]);
  const pointMeshesRef = useRef([]);

  // Build or update the group and its children whenever dependencies change
  useEffect(() => {
    console.debug('[EmbeddingsGroup][useEffect] Triggered with dependencies:', {
      enrichedChunksLen: enrichedChunks.length,
      displayParam_tokenColorMode,
      displayParam_labelColorMode,
      displayParam_fogColorMode,
      displayParam_environmentColorMode,
      showLabels,
      textSize,
      dotSize,
      labelOffset,
      showPathMode,
      pointStyle
    });
    const scene = sceneRef.current;
    if (!scene) {
      console.warn('[EmbeddingsGroup][useEffect] No scene found in sceneRef');
      return;
    }

    // --- FOG COLOR HANDLING ---
    // Remove fog if mode is NONE, otherwise set fog color
    let fogColor = null;
    if (displayParam_fogColorMode === DisplayParamFogColorMode.NONE) {
      scene.fog = null;
    } else {
      // Use DisplayParamColors for fog color
      fogColor = DisplayParamColors[displayParam_fogColorMode.toUpperCase()] || DisplayParamColors.GREY;
      scene.fog = new THREE.Fog(fogColor, 2, 5);
    }

    // --- ENVIRONMENT COLOR HANDLING ---
    // Always set background color based on environment color mode
    const envColor = DisplayParamColors[displayParam_environmentColorMode.toUpperCase()] || DisplayParamColors.GREY;
    scene.background = new THREE.Color(envColor);


    // --- AXES HELPER UPDATE ---
    // If a ThreeSceneManager ref is provided, update axes helper startColor and endColor for contrast
    console.debug('[EmbeddingsGroup][useEffect] Checking for ThreeSceneManager ref');
    if (threeSceneManagerRef && threeSceneManagerRef.current && typeof threeSceneManagerRef.current.updateAxesHelper_color === 'function') {
      let startColor = DisplayParamColors.WHITE;
      let endColor = DisplayParamColors.BLACK;
      if (displayParam_environmentColorMode === DisplayParamEnvironmentColorMode.WHITE) {
        startColor = DisplayParamColors.BLACK;
        endColor = DisplayParamColors.WHITE;
      } else if (displayParam_environmentColorMode === DisplayParamEnvironmentColorMode.DARK_BLUE) {
        startColor = DisplayParamColors.WHITE;
        endColor = DisplayParamColors.DARK_BLUE;
      } else if (displayParam_environmentColorMode === DisplayParamEnvironmentColorMode.BLACK) {
        startColor = DisplayParamColors.WHITE;
        endColor = DisplayParamColors.BLACK;
      }
      console.debug('[EmbeddingsGroup][useEffect] Updating axes helper colors:', { startColor, endColor });
      threeSceneManagerRef.current.updateAxesHelper_color({ startColor, endColor });
    }
    else{
      console.warn('[EmbeddingsGroup][useEffect] No ThreeSceneManager ref found, skipping axes helper update');
    }
    // --- GROUP SETUP ---
    // Create group if not present
    if (!groupRef.current) {
      const group = new THREE.Group();
      group.name = 'embeddingsGroup';
      scene.add(group);
      groupRef.current = group;
      console.debug('[EmbeddingsGroup][useEffect] Created new group and added to scene');
    }
    const group = groupRef.current;
    // Clear group (remove all children and dispose geometries/materials)
    while (group.children.length > 0) {
      const obj = group.children[0];
      if (obj.geometry) obj.geometry.dispose();
      if (obj.material) {
        if (Array.isArray(obj.material)) {
          obj.material.forEach(mat => mat.dispose && mat.dispose());
        } else {
          obj.material.dispose && obj.material.dispose();
        }
      }
      group.remove(obj);
    }
    console.debug('[EmbeddingsGroup][useEffect] Cleared group children');
    labelMeshesRef.current = [];
    pointMeshesRef.current = [];
    // Set transform (reset position, rotation, scale)
    group.position.set(0, 0, 0);
    group.rotation.set(0, 0, 0);
    group.scale.set(1, 1, 1);
    // Points as sprites (CustomPointHelper)
    if (enrichedChunks.length > 0) {
      console.debug('[EmbeddingsGroup][useEffect] Adding points with pointStyle:', pointStyle, 'dotSize:', dotSize);
      const points = enrichedChunks.map((chunk, i) => {
        const pca = chunk.chunkEmbeddingReduced?.fullEmbedding;
        if (!pca) return null;
        let pointColor_hex;
        if (displayParam_tokenColorMode === DisplayParamTokenColorMode.TOKEN_ID) {
          pointColor_hex = getTokenColor(chunk.chunkTokens[0]?.id);
        } else if (displayParam_tokenColorMode === DisplayParamTokenColorMode.SEQUENCE) {
          pointColor_hex = getSequenceColor(i, enrichedChunks.length);
        } else if (
          displayParam_tokenColorMode === DisplayParamTokenColorMode.BLACK ||
          displayParam_tokenColorMode === DisplayParamTokenColorMode.WHITE ||
          displayParam_tokenColorMode === DisplayParamTokenColorMode.DARK_BLUE
        ) {
          pointColor_hex = DisplayParamColors[displayParam_tokenColorMode.toUpperCase()] || DisplayParamColors.GREY;
        } else if (displayParam_tokenColorMode === DisplayParamTokenColorMode.NONE) {
          pointColor_hex = DisplayParamColors.RED;
        } else {
          pointColor_hex = DisplayParamColors.GREY;
        }
        return {
          position: [pca[0], pca[1], pca[2]],
          color: pointColor_hex
        };
      }).filter(Boolean);
      const spriteSize = dotSize * 0.05;
      const pointsObj = new CustomPointHelper(points, { size: spriteSize, pointStyle });
      group.add(pointsObj);
      pointMeshesRef.current.push(pointsObj);
      console.debug('[EmbeddingsGroup][useEffect] Added CustomPointHelper to group');
    }
    // Lines (straight segments between points)
    let linesObj = null;
    if (enrichedChunks.length > 1) {
      console.debug('[EmbeddingsGroup][useEffect] Adding lines and/or curve');
      const linePositions = [];
      const lineColors = [];
      enrichedChunks.forEach((chunk, i) => {
        const pca = chunk.chunkEmbeddingReduced?.fullEmbedding;
        const nextPca = enrichedChunks[i + 1]?.chunkEmbeddingReduced?.fullEmbedding;
        if (!pca || !nextPca) return;
        if (i < enrichedChunks.length - 1) {
          let lineColor_hex;
          if (displayParam_tokenColorMode === DisplayParamTokenColorMode.TOKEN_ID) {
            lineColor_hex = getTokenColor(chunk.chunkTokens[0]?.id);
          } else if (displayParam_tokenColorMode === DisplayParamTokenColorMode.SEQUENCE) {
            lineColor_hex = getSequenceColor(i, enrichedChunks.length);
          } else if (
            displayParam_tokenColorMode === DisplayParamTokenColorMode.BLACK ||
            displayParam_tokenColorMode === DisplayParamTokenColorMode.WHITE ||
            displayParam_tokenColorMode === DisplayParamTokenColorMode.DARK_BLUE
          ) {
            lineColor_hex = DisplayParamColors[displayParam_tokenColorMode.toUpperCase()] || DisplayParamColors.GREY;
          } else if (displayParam_tokenColorMode === DisplayParamTokenColorMode.NONE) {
            lineColor_hex = DisplayParamColors.RED;
          } else {
            lineColor_hex = DisplayParamColors.GREY;
          }
          const c = new THREE.Color(lineColor_hex);
          linePositions.push(pca[0], pca[1], pca[2], nextPca[0], nextPca[1], nextPca[2]);
          lineColors.push(c.r, c.g, c.b, c.r, c.g, c.b);
        }
      });
      const lineGeom = new THREE.BufferGeometry();
      lineGeom.setAttribute('position', new THREE.Float32BufferAttribute(linePositions, 3));
      lineGeom.setAttribute('color', new THREE.Float32BufferAttribute(lineColors, 3));
      const lineMat = new THREE.LineBasicMaterial({
        vertexColors: true,
        opacity: 0.7,
        transparent: true,
        side: THREE.DoubleSide,
      });
      linesObj = new THREE.LineSegments(lineGeom, lineMat);
      linesObj.visible = (showPathMode === 'lines');
      group.add(linesObj);
    }
    // Curve (Catmull-Rom spline through all points)
    let splineLine = null;
    if (enrichedChunks.length > 1) {
      const curvePoints = enrichedChunks
        .map(chunk => chunk.chunkEmbeddingReduced?.fullEmbedding)
        .filter(Boolean)
        .map(pca => new THREE.Vector3(pca[0], pca[1], pca[2]));
      if (curvePoints.length > 1) {
        const curve = new THREE.CatmullRomCurve3(curvePoints, false, 'catmullrom', 0.35);
        const numCurvePoints = Math.max(2, curvePoints.length * 10);
        const splinePoints = curve.getPoints(numCurvePoints);
        const splineGeometry = new THREE.BufferGeometry().setFromPoints(splinePoints);
        const splineColors = [];
        for (let i = 0; i < splinePoints.length; i++) {
          const chunkIdx = Math.round(i * (enrichedChunks.length - 1) / (splinePoints.length - 1));
          let splineColor_hex;
          if (displayParam_tokenColorMode === DisplayParamTokenColorMode.TOKEN_ID) {
            splineColor_hex = getTokenColor(enrichedChunks[chunkIdx]?.chunkTokens[0]?.id);
          } else if (displayParam_tokenColorMode === DisplayParamTokenColorMode.SEQUENCE) {
            splineColor_hex = getSequenceColor(chunkIdx, enrichedChunks.length);
          } else if (
            displayParam_tokenColorMode === DisplayParamTokenColorMode.BLACK ||
            displayParam_tokenColorMode === DisplayParamTokenColorMode.WHITE ||
            displayParam_tokenColorMode === DisplayParamTokenColorMode.DARK_BLUE
          ) {
            splineColor_hex = DisplayParamColors[displayParam_tokenColorMode.toUpperCase()] || DisplayParamColors.GREY;
          } else if (displayParam_tokenColorMode === DisplayParamTokenColorMode.NONE) {
            splineColor_hex = DisplayParamColors.RED;
          } else {
            splineColor_hex = DisplayParamColors.GREY;
          }
          const c = new THREE.Color(splineColor_hex);
          splineColors.push(c.r, c.g, c.b);
        }
        splineGeometry.setAttribute('color', new THREE.Float32BufferAttribute(splineColors, 3));
        const splineMaterial = new THREE.LineBasicMaterial({
          vertexColors: true,
                  vertexColors: true,
        opacity: 0.7,
        transparent: true,
          linewidth: 1,
blending: THREE.NormalBlending,
depthWrite: true,
depthTest: true,
          side: THREE.DoubleSide,
        });
        splineLine = new THREE.Line(splineGeometry, splineMaterial);
        splineLine.visible = (showPathMode === 'curve');
        group.add(splineLine);
      }
    }
    // Labels (first token of each chunk, always face camera)
    if (showLabels) {
      console.debug('[EmbeddingsGroup][useEffect] Adding labels with textSize:', textSize, 'labelOffset:', labelOffset);
      enrichedChunks.forEach((chunk, i) => {
        const pca = chunk.chunkEmbeddingReduced?.fullEmbedding;
        if (!pca) return;
        const firstToken = Array.isArray(chunk.chunkTokens) && chunk.chunkTokens.length > 0 ? chunk.chunkTokens[0]?.text || '' : '';
        let labelColor_hex;
        if (displayParam_labelColorMode === DisplayParamLabelColorMode.TOKEN_ID) {
          labelColor_hex = getTokenColor(chunk.chunkTokens[0]?.id);
        } else if (displayParam_labelColorMode === DisplayParamLabelColorMode.SEQUENCE) {
          labelColor_hex = getSequenceColor(i, enrichedChunks.length);
        } else if (
          displayParam_labelColorMode === DisplayParamLabelColorMode.BLACK ||
          displayParam_labelColorMode === DisplayParamLabelColorMode.WHITE ||
          displayParam_labelColorMode === DisplayParamLabelColorMode.DARK_BLUE
        ) {
          labelColor_hex = DisplayParamColors[displayParam_labelColorMode.toUpperCase()] || DisplayParamColors.GREY;
        } else if (displayParam_labelColorMode === DisplayParamLabelColorMode.NONE) {
          labelColor_hex = DisplayParamColors.RED;
        } else {
          labelColor_hex = DisplayParamColors.GREY;
        }
        const c = new THREE.Color(labelColor_hex);
        const textMesh = new Text();
        textMesh.text = firstToken;
        textMesh.fontSize = textSize * 0.0125;
        textMesh.font = robotoFontUrl;
        textMesh.color = c.getStyle();
        textMesh.anchorX = 'center';
        textMesh.anchorY = 'bottom';
        textMesh.position.set(pca[0], (pca[1] + labelOffset) - (textMesh.fontSize / 1.5), pca[2]);
        textMesh.frustumCulled = false;
        textMesh.userData.isLabel = true;
        textMesh.userData.chunkIndex = i;
        const prevOnBeforeRender = textMesh.onBeforeRender;
        textMesh.onBeforeRender = function(renderer, scene, camera, ...args) {
          if (typeof prevOnBeforeRender === 'function') {
            prevOnBeforeRender.call(this, renderer, scene, camera, ...args);
          }
          let lookTarget = new THREE.Vector3();
          if (camera.isArrayCamera && camera.cameras.length >= 2) {
            // VR: use midpoint between left and right eye cameras
            const left = new THREE.Vector3();
            const right = new THREE.Vector3();
            camera.cameras[0].getWorldPosition(left);
            camera.cameras[1].getWorldPosition(right);
            lookTarget.copy(left).add(right).multiplyScalar(0.5);
          } else {
            // Non-VR: use single camera
            camera.getWorldPosition(lookTarget);
          }
          this.up.set(0, 1, 0);
          this.lookAt(lookTarget);
        };
        group.add(textMesh);
        textMesh.sync();
        labelMeshesRef.current.push(textMesh);
      });
    }
    // Attach group to scene if not already
    if (group.parent !== scene) {
      if (group.parent) group.parent.remove(group);
      scene.add(group);
      console.debug('[EmbeddingsGroup][useEffect] Attached group to scene');
    }
  }, [sceneRef, threeSceneManagerRef, enrichedChunks, displayParam_tokenColorMode, displayParam_fogColorMode, displayParam_environmentColorMode, displayParam_labelColorMode, showLabels, textSize, dotSize, labelOffset, showPathMode, getTokenColor, getSequenceColor, pointStyle]);

  // No JSX output; this is a Three.js imperative component
  return null;
}

export default EmbeddingsGroup;
